import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import authenticateService from '../../services/authenticateService';
import { RHFValidations, TOAST_MESSAGES } from '../../constants/global-constants';
import CustomInputBox from '../../controls/CustomInputBox';
import Button from '../../controls/Button';
import { AwsLoginErrors } from '../../constants/telosair-interfaces';

type FORMDATA = {
  username: string;
};

/**
 * ForgotPassword.
 * @returns - React component for ForgotPassword UI.
 */
export default function ForgotPasswordComponent(): ReactElement {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const methods = useForm<FORMDATA>({
    mode: 'all'
  });
  const { handleSubmit } = methods;

  /**
   * Handles forgot password.
   * @param  data - The form data.
   */
  const handleForgetPassword = async (data: FORMDATA): Promise<void> => {
    const { username } = data;
    setLoading(true);
    try {
      const { error } = await authenticateService.forgotPassword(username);
      if (!error) {
        toast.success('Verification code sent successfully');
        navigate(`/auth/reset-password?username=${encodeURIComponent(username)}`);
        return;
      }
      if (error === AwsLoginErrors.notFound) {
        toast.error('User not found');
        setLoading(false);
        return;
      }
      setLoading(false);
      toast.error(TOAST_MESSAGES.error);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast.error(TOAST_MESSAGES.error);
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-8 w-full">
      <div className="text-3xl font-bold dark:text-white text-black text-center">
        Welcome to TelosAir
      </div>
      {/* <div className="text-gray-400 mt-1">Start managing your devices for free</div> */}
      <div className="dark:bg-container-700 bg-white p-6 rounded w-full">
        <div className="flex gap-3 items-center">
          <button
            onClick={() => navigate(-1)}
            type="button"
            className="font-medium text-gray-300 text-xl material-icons-outlined"
          >
            arrow_back
          </button>
          <div className="text-xl dark:text-white text-black font-semibold">Forgot password</div>
        </div>
        <div className="text-gray-400 mt-1">
          Please enter the email address for your account. A verification code will be send to you.
          Once you have received the verification code. you will be able to choose a new password
          for your account.
        </div>
        {/* eslint-disable-next-line */}
        <FormProvider {...methods}>
          <form
            className="mt-8 2xl:mt-12"
            action="post"
            onSubmit={handleSubmit(handleForgetPassword)}
          >
            <CustomInputBox
              label="Email"
              idname="username"
              autoComplete="email"
              type="email"
              validationObject={RHFValidations.emailRequired}
            />
            <Button
              loading={loading}
              name="Reset Password"
              className="mt-6"
              size="md"
              buttonType="submit"
              theme="primary"
              isBlockButton
            />
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
